import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import { Box, Grid, Paper } from "@mui/material";
import type { NamedColor } from "components/filter/color-filter/ColorFilter";
import { SearchResultsGridLoadingTile } from "components/search-results/grid/SearchResultsGridLoadingTile";
import { SearchResultsGridTile } from "components/search-results/grid/SearchResultsGridTile";
import { NoSearchResults } from "components/search-results/NoSearchResults";
import { SearchResultsTable } from "components/search-results/table/SearchResultsTable";
import { useAuthUser } from "hooks/authentication";
import type { SortEntry } from "hooks/sort";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import { useContext } from "react";
import { checkInternalUser } from "service/utils/get-user-type";
import type { ResultsViewMode } from "../../types";
import { MatchPercentBadge } from "./MatchPercentBadge";

interface MatchedMaterialsGridProps {
  values?: ColoredMaterial[];
  loading?: boolean;
  viewMode?: ResultsViewMode;
  gridSize?: number;
  sort?: SortEntry[];
  onSortChange?: (sort: SortEntry[]) => void;
  onMaterialSelect?: (material: ColoredMaterial) => void;
  onFilterReset?: () => void;
  target?: NamedColor;
}

export const MatchedMaterialsGrid = ({
  values,
  loading,
  viewMode = "grid",
  gridSize = 2,
  sort,
  onSortChange,
  onMaterialSelect,
  onFilterReset,
  target,
}: MatchedMaterialsGridProps) => {
  const { settings } = useContext<SettingsContextValue>(SettingsContext);
  const { user } = useAuthUser();
  const isInternal = checkInternalUser(user, settings);
  const imageType = gridSize > 2 ? "THUMBNAIL" : "SQUARE";

  if (viewMode === "list") {
    return (
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          borderRadius: "var(--radius-card, 8px)",
          border: "1px solid var(--alias-stroke-on-white-default, #d9d9d9)",
          boxShadow: "0px 4px 12px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        <SearchResultsTable
          values={values}
          loading={loading}
          sort={sort}
          onSortChange={onSortChange}
          onFilterReset={onFilterReset}
          onMaterialSelect={onMaterialSelect}
          target={target}
        />
      </Paper>
    );
  }

  // Grid view reuses the shared CIE-Lab card (SearchResultsGridTile) for
  // identical styling/spacing, but overlays a match-percentage badge instead
  // of passing `target` (which would render the raw ΔE value).
  return (
    <Grid
      sx={{ width: "100%" }}
      container
      spacing={2}
      columns={{
        xxl: gridSize + 4,
        xl: gridSize + 3,
        lg: gridSize + 2,
        md: gridSize + 2,
        sm: gridSize + 1,
        xs: gridSize,
      }}
    >
      {loading &&
        Array.from(Array(14)).map((_, i) => (
          <Grid key={`skeleton-${i}`} size={1}>
            <SearchResultsGridLoadingTile imageAspectRatio="1 / 1" />
          </Grid>
        ))}
      {!loading &&
        values?.map((material) => (
          <Grid
            key={material.eljExperimentId + material.eljExperimentBatch}
            size={1}
          >
            <Box sx={{ position: "relative", height: "100%" }}>
              <SearchResultsGridTile
                material={material}
                isInternal={isInternal}
                imageAspectRatio="1 / 1"
                imageType={imageType}
                onMaterialSelect={onMaterialSelect}
              />
              {material.color.specifications?.deltaE != null && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    zIndex: 1,
                  }}
                >
                  <MatchPercentBadge
                    deltaE={material.color.specifications.deltaE}
                  />
                </Box>
              )}
            </Box>
          </Grid>
        ))}
      {!loading && values?.length === 0 && (
        <Grid size={12} fontSize="var(--body-b2-14-regular-size, 14px)">
          <Paper sx={{ padding: 2 }}>
            <NoSearchResults onFilterReset={onFilterReset} />
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};
